import {
  Button,
  Divider,
  Grid,
  Typography,
  Paper,
  TextField,
  useMediaQuery,
  Box,
  Stack,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { makeStyles } from '@mui/styles';
import { environment } from 'apps/table-tuck-web/src/environments/environment';
import { useDispatch, useSelector } from 'react-redux';
import { setCart } from '../../../../../../libs/redux-slices/src';
import TipsBox from './TipsBox';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import FeesDialog from '../Dialouge/FeesDailoge';
import ReusableDialog from '../Dialouge/ReusableDailoge';
import { fetchData } from '../../Utils/UtilsFunctions';
import { auth } from '@tabletuck/firebase-services';
import SquareGiftCard from '../SquareGiftCard/SquareGiftCard';

import { formatPriceFromCents } from '../../Utils/UtilsFunctions';

const useStyles = makeStyles((theme) => ({
  typographyH6: {
    flex: 1,
    color: '#172B4D',
    fontWeight: 700,
    fontSize: '14px',
  },
  typographyP: {
    flex: 1,
    fontSize: '14px',
    color: '#4F4F4F',
    textTransform: 'capitalize',
    fontWeight: 500,
  },
}));

const OrderSummary = ({
  typeTip,
  setTypeTip,
  otherTip,
  setOtherTip,
  setTipsAmount,
  tipAmount,
  delivery_method,
  defaultStore,
  defaultStorefee,
  filteredStore,
  storeIdD,
  onTipChange,
  giftCardCode,
  setGiftCardCode,
  appliedGiftCard,
  setAppliedGiftCard,
  onGiftCardApply,
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const lgDown = useMediaQuery(theme.breakpoints.down('lg'));
  const [data, setData] = useState([]);
  const [store, setStore] = React.useState([]);
  const [addressStore, setAddressStore] = useState([]);
  const [fees, setFees] = useState(0);
  const currentFees = useSelector((state) => state.shopReducer.currentFees);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [openDeliveryfees, setOpenDeliveryfees] = useState(false);
  const [opentaxes, setOpentaxes] = useState(false);
  const [dataStore, setDataStore] = useState([]);
  const dispatch = useDispatch();
  const [t, i18n] = useTranslation();
  const addresses = useSelector((state) => state.authReducer.userAddresses);
  const userData = useSelector((state) => state.authReducer.userData);
  const stores = useSelector((state) => state.shopReducer.stores);
  const [serviceFees, setServiceFees] = useState(0);

  // Gift card states and ref
  const giftCardRef = useRef(null);
  const [isApplyingGiftCard, setIsApplyingGiftCard] = useState(false);
  const [giftCardError, setGiftCardError] = useState('');
  const [giftCardToken, setGiftCardToken] = useState(null);

  // Memoize the gift card key to only change when store ID actually changes
  const giftCardKey = useMemo(() => {
    const effectiveStoreId = storeIdD || userData?.defaultStore;
    return `giftcard-summary-${effectiveStoreId}`;
  }, [storeIdD, userData?.defaultStore]);

  // Stabilize callbacks with useCallback to prevent prop changes
  const handleGiftCardToken = useCallback((token) => {
    setGiftCardToken(token);
    setGiftCardError('');
  }, []);

  const handleGiftCardError = useCallback((error) => {
    setGiftCardError(error.message || 'Gift Card processing failed');
  }, []);

  // Read pricing data directly from Redux for real-time updates
  const [isCalculatingPrice, setIsCalculatingPrice] = useState(false);

  const {
    orderType,
    subTotal: total = 0,
    discount = 0,
    isVerify,
    apiPricingData,
  } = useSelector((state) => state.shopReducer.cartData);

  // Track if pricing data is being updated (for loader)
  const [prevApiPricingData, setPrevApiPricingData] = useState(null);
  const [isDataChanging, setIsDataChanging] = useState(false);

  // Effect to detect when pricing data is changing
  useEffect(() => {
    if (prevApiPricingData && apiPricingData) {
      // Check if pricing data has changed significantly
      const hasChanged =
        prevApiPricingData.total !== apiPricingData.total ||
        prevApiPricingData.subtotal !== apiPricingData.subtotal ||
        prevApiPricingData.tax !== apiPricingData.tax ||
        (prevApiPricingData.loyaltyDiscount || 0) !==
          (apiPricingData.loyaltyDiscount || 0) ||
        prevApiPricingData.discount !== apiPricingData.discount;

      if (hasChanged) {
        setIsDataChanging(true);
        // Hide loader after a short delay to show the update
        const timer = setTimeout(() => {
          setIsDataChanging(false);
        }, 500);
        return () => clearTimeout(timer);
      }
    }
    setPrevApiPricingData(apiPricingData);
  }, [apiPricingData]);



  const licenses = useSelector((state) => state.licenReducer.licenses);

  const checkLicenses = (pageName) => {
    return licenses?.licenses?.frontend?.[pageName];
  };

  const getUserAddresses = async () => {
    const bags = userData?.bagItems;
    bags.length && dispatch(setCart(bags));
    const defaultDeliveryAddress = userData.defaultDeliveryAddress;

    try {
      await Promise.all(
        addresses?.map(async (a) => {
          if (a?.id === defaultDeliveryAddress) {
            setAddressStore(a?.delivery_store_id);
          }
        })
      );
    } catch (error) {
      // Error handled silently
    }
  };

  const getStores = () => {
    stores?.map((store) => {
      if (store?.id === addressStore) {
        setData(store);
      }

      if (store?.delivery_method === 'local') {
        setFees(store?.local_delivery_fee.fixed_fees);
      } else setFees(store?.doordash_delivery_fees);
    });
  };

  // Custom tip handler that calls API
  const handleTipChange = (newTypeTip, customTipAmount = null) => {
    setTypeTip(newTypeTip);

    // If custom tip amount is provided (for 'other' type), use it directly
    let calculatedTip;
    if (newTypeTip === 'other' && customTipAmount !== null) {
      calculatedTip = customTipAmount;
      // Also update otherTip state
      setOtherTip(customTipAmount);
    } else {
      calculatedTip = calculateTipAmount(newTypeTip);
    }

    setTipsAmount(calculatedTip);

    // Always call parent's tip change handler to trigger API call
    if (onTipChange) {
      onTipChange(calculatedTip);
    }
  };

  // Helper function to calculate tip amount without side effects
  const calculateTipAmount = (tipType) => {
    // Always use fixed percentages [10%, 15%, 20%]
    const percentages = [10, 15, 20];

    // Get current order total from API pricing data (in cents)
    const currentTotal = apiPricingData ? apiPricingData.total : total * 100;

    if (tipType === 'none') {
      return 0;
    } else if (tipType === 'a') {
      const tipA = Math.round((currentTotal * percentages[0]) / 100);
      return tipA;
    } else if (tipType === 'b') {
      const tipB = Math.round((currentTotal * percentages[1]) / 100);
      return tipB;
    } else if (tipType === 'c') {
      const tipC = Math.round((currentTotal * percentages[2]) / 100);
      return tipC;
    } else if (tipType === 'other') {
      return parseFloat(otherTip) || 0;
    }

    return 0;
  };

  // Helper functions to get pricing from API or fallback to calculations
  const getDisplaySubtotal = () => {
    if (apiPricingData && apiPricingData.subtotal !== undefined) {
      return formatPriceFromCents(apiPricingData.subtotal);
    }
    return isVerify
      ? formatPriceFromCents(total - discount)
      : formatPriceFromCents(total);
  };

  const getDisplayTax = () => {
    if (apiPricingData && apiPricingData.tax !== undefined) {
      return formatPriceFromCents(apiPricingData.tax);
    }
    // Fallback to existing tax calculations
    return '0.00';
  };
  const getDisplayDelivery = () => {
    if (apiPricingData && apiPricingData.serviceChargeMoney !== undefined) {
      return formatPriceFromCents(
        apiPricingData.serviceChargeMoney - apiPricingData.onlineFee
      );
    }
    // Fallback to existing  calculations
    return '0.00';
  };
  const getDisplayTotal = () => {
    // If gift card is applied, use remainingAmount instead of totalWithTip
    if (
      apiPricingData &&
      apiPricingData.giftCardBalance > 0 &&
      apiPricingData.remainingAmount !== undefined
    ) {
      return formatPriceFromCents(apiPricingData.remainingAmount);
    }
    if (apiPricingData && apiPricingData.totalWithTip !== undefined) {
      // Use backend's totalWithTip (includes tip already)
      return formatPriceFromCents(apiPricingData.totalWithTip);
    }
    if (apiPricingData && apiPricingData.total !== undefined) {
      // Fallback to total without tip if totalWithTip not available
      return formatPriceFromCents(apiPricingData.total + (tipAmount || 0));
    }

    // Fallback to local totals when API pricing is unavailable
    const fallbackTotal =
      (isVerify ? total - discount : total) + (tipAmount || 0);
    return formatPriceFromCents(fallbackTotal);
  };
  const getDisplayTotalDiscount = () => {
    if (apiPricingData && apiPricingData.total_discounts !== undefined) {
      return formatPriceFromCents(apiPricingData.total_discounts);
    }
    // Fallback to existing total calculations only if no API data
    return formatPriceFromCents(discount);
  };

  // Gift card apply handler - tokenizes with Square SDK then calls API
  const handleApplyGiftCard = async () => {
    if (!giftCardRef.current) {
      setGiftCardError('Gift card component not initialized');
      return;
    }

    setIsApplyingGiftCard(true);
    setGiftCardError('');

    try {
      // Tokenize the gift card using Square SDK
      const token = await giftCardRef.current.tokenize();

      setGiftCardToken(token);

      // Call the parent's calculatePriceWithUpdates with the gift card nonce
      await onGiftCardApply({ giftCardNonce: token });

      // If successful, mark the gift card as applied
      setAppliedGiftCard({
        code: token,
        // The balance/amount will be reflected in the API pricing response
      });

      setGiftCardError('');
    } catch (error) {
      setGiftCardError(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to apply gift card. Please check the code and try again.'
      );
    } finally {
      setIsApplyingGiftCard(false);
    }
  };

  // Handler to remove applied gift card
  const handleRemoveGiftCard = async () => {
    setAppliedGiftCard(null);
    setGiftCardCode('');
    setGiftCardToken(null);
    setGiftCardError('');

    // Clear the Square Gift Card component
    if (giftCardRef.current) {
      try {
        giftCardRef.current.clear();
      } catch (e) {
        // Error handled silently
      }
    }

    // Recalculate prices without gift card
    try {
      await onGiftCardApply({ giftCardNonce: null });
    } catch (error) {
      // Error handled silently
    }
  };

  const getUser = async () => {
    const bags = userData?.bagItems;
    bags.length && dispatch(setCart(bags));
    const storeId = userData?.defaultStore;
    stores?.map((store) => {
      if (store?.id === storeId) {
        setStore(store?.businessName);
      }
    });
  };
  const getStore = async () => {
    // Don't overwrite dataStore for delivery orders - it's handled separately
    if (orderType === 'delivery') {
      return;
    }
    stores?.map((storee) => {
      if (storee?.businessName === store) {
        setDataStore(storee);
      }
    });
  };

  // Set dataStore for delivery orders based on addressStore or storeIdD
  useEffect(() => {
    

    if (orderType === 'delivery') {
      // First try filteredStore if available
      if (filteredStore && filteredStore.id) {
       
        setDataStore(filteredStore);
        return;
      }
      // Then try to find store by addressStore
      if (addressStore) {
        const foundStore = stores?.find(
          (storee) => storee?.id === addressStore
        );
        if (foundStore) {
          
          setDataStore(foundStore);
          return;
        }
      }
      // Finally try to find store by storeIdD
      if (storeIdD) {
        const foundStore = stores?.find((storee) => storee?.id === storeIdD);
        if (foundStore) {
         
          setDataStore(foundStore);
          return;
        }
      }
      // Fallback to data if it's set and has an id
      if (data && data.id) {
       
        setDataStore(data);
      }
    }
    
  }, [orderType, filteredStore, addressStore, storeIdD, stores, data]);

  useEffect(() => {
    setDeliveryFee(currentFees);
  }, [currentFees]);

  useEffect(() => {
    getUserAddresses();
    getStores();
  }, [addressStore]);

  useEffect(() => {
    getUser();
  }, []);

  useEffect(() => {
    getStore();
  }, [store, dataStore]);

  useEffect(() => {
    if (userData?.pickupOrDelivery === 'dinein') {
      setServiceFees(dataStore?.service_fee?.dineIn);
    } else if (userData?.pickupOrDelivery === 'pickup') {
      setServiceFees(dataStore?.service_fee?.pickUp);
    } else if (userData?.pickupOrDelivery === 'delivery') {
      setServiceFees(dataStore?.service_fee?.delivery);
    }
  }, [userData, userData?.pickupOrDelivery, store]);

  // Display helpers: hide tax/tip rows when they are zero (but keep if calculating)
  const displayTaxStr =
    isCalculatingPrice || isDataChanging ? 'Calculating...' : getDisplayTax();
  const showTax =
    isCalculatingPrice ||
    isDataChanging ||
    (displayTaxStr && displayTaxStr !== '0.00');
  const displayTipAmountStr =
    isCalculatingPrice || isDataChanging
      ? 'Calculating...'
      : formatPriceFromCents(tipAmount || 0);
  const showTip = Number(tipAmount || 0) > 0;
  return (
    <Stack gap={3}>
      {orderType !== 'dinein'
        ? checkLicenses('tips') && (
            <Grid item style={{}} xs={12}>
              <Typography
                component="p"
                variant="h6"
                sx={{
                  fontWeight: '700',
                  color: '#172B4D',
                  fontSize: '20px',
                  fontFamily: 'Jost',
                  marginBottom: '10px',
                  textTransform: 'capitalize',
                }}
              >
                add a tip
              </Typography>
              <TipsBox
                key={`tipsbox-${apiPricingData?.total || 0}-${typeTip}`}
                typeTip={typeTip}
                setTypeTip={handleTipChange}
                environment={environment}
                total={total}
                fees={fees}
                t={t}
                filteredStore={filteredStore}
                apiPricingData={apiPricingData}
                setOtherTip={setOtherTip}
                otherTip={otherTip}
              />
              <Typography
                component="p"
                variant="body2"
                style={{ color: 'gray' }}
              >
                {orderType === 'delivery' ? t('confirmPage.tipsDetails') : ''}
              </Typography>
            </Grid>
          )
        : ''}

      {/* Gift Card Section */}
      <Grid item xs={12}>
        <Typography
          component="p"
          variant="h6"
          sx={{
            fontWeight: '700',
            color: '#172B4D',
            fontSize: '20px',
            fontFamily: 'Jost',
            marginBottom: '10px',
            textTransform: 'capitalize',
          }}
        >
          Gift Card
        </Typography>

        {!appliedGiftCard ? (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'stretch',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                width: '100%',
                p: 2.5,
                backgroundColor: 'white',
                borderRadius: '12px',
                border: '2px solid #E6E6E6',
              }}
            >
              <SquareGiftCard
                key={giftCardKey}
                ref={giftCardRef}
                onToken={handleGiftCardToken}
                onError={handleGiftCardError}
                currentStoreId={storeIdD || userData?.defaultStore}
              />
            </Box>

            <Button
              onClick={handleApplyGiftCard}
              disabled={isApplyingGiftCard}
              sx={{
                borderRadius: '10px',
                // padding: '12px 20px',
                backgroundColor: environment.mainColor,
                color: 'white',
                fontFamily: 'Jost, sans-serif',
                fontWeight: 600,
                fontSize: '15px',
                textTransform: 'none',
                alignSelf: 'center',
                minWidth: '200px',
                maxWidth: '300px',
                '&:hover': {
                  backgroundColor: environment.mainColor,
                  opacity: 0.9,

                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                },
                '&:disabled': {
                  backgroundColor: '#E6E6E6',
                  color: '#999',
                },
                transition: 'all 0.2s ease',
                textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
              }}
            >
              {isApplyingGiftCard ? (
                <CircularProgress size={18} sx={{ color: 'white' }} />
              ) : (
                'Apply Gift Card'
              )}
            </Button>

            {giftCardError && (
              <Typography
                variant="caption"
                sx={{
                  color: '#EF4444',
                  fontFamily: 'Jost, sans-serif',
                  fontSize: '13px',
                  textAlign: 'center',
                  px: 2,
                }}
              >
                {giftCardError}
              </Typography>
            )}

            <Typography
              variant="caption"
              sx={{
                color: '#6B7280',
                fontFamily: 'Jost, sans-serif',
                fontSize: '12px',
                textAlign: 'center',
                px: 1,
              }}
            >
              💡 Enter the code found on your gift card above
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              backgroundColor: '#F0FDF4',
              borderRadius: '12px',
              border: '2px solid #86EFAC',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                sx={{
                  fontSize: '24px',
                }}
              >
                🎁
              </Typography>
              <Box>
                <Typography
                  sx={{
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 600,
                    fontSize: '14px',
                    color: '#166534',
                  }}
                >
                  Gift Code Applied
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Jost, sans-serif',
                    fontSize: '12px',
                    color: '#15803D',
                  }}
                >
                  Code:{' '}
                  {apiPricingData?.giftCardDetails?.gan || appliedGiftCard.code}
                </Typography>
              </Box>
            </Box>
            <Button
              onClick={handleRemoveGiftCard}
              sx={{
                color: '#DC2626',
                fontFamily: 'Jost, sans-serif',
                fontWeight: 600,
                fontSize: '14px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#FEE2E2',
                },
              }}
            >
              Remove
            </Button>
          </Box>
        )}
      </Grid>

      {/* <Typography component="p" variant="h5" style={{ fontWeight: 'bold' }}>
        {t('confirmPage.Ordersummary')}
      </Typography>
      <Divider /> */}

      <div
        style={{
          backgroundColor: '#FAFAFA',
          borderRadius: '16px',
          padding: '16px',
          position: 'relative',
        }}
      >
        {/* Loading overlay */}
        {isDataChanging && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(250, 250, 250, 0.8)',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress
                size={24}
                sx={{ color: environment.mainColor }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  color: '#666',
                }}
              >
                Updating prices...
              </Typography>
            </Box>
          </Box>
        )}
        <Typography
          className={classes.typographyP}
          variant="body1"
          component="p"
          sx={{
            marginBottom: '16px',
            fontWeight: 500,
            color: '#172B4D',
            fontSize: '18px',
            fontFamily: 'Jost, sans-serif',
            textTransform: 'capitalize',
          }}
        >
          order summary
        </Typography>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <div style={{ flex: 1 }}>
            <Typography
              className={classes.typographyP}
              variant="body2"
              component="p"
              sx={{ fontFamily: 'Jost, sans-serif' }}
            >
              {t('basket.subtotal')}
            </Typography>
          </div>
          <div style={{ flex: 0.4, textAlign: 'right' }}>
            <Typography
              className={classes.typographyH6}
              variant="body2"
              sx={{ fontFamily: 'Jost, sans-serif' }}
            >
              {environment.currency}
              {isCalculatingPrice || isDataChanging ? (
                <Skeleton
                  variant="text"
                  width={60}
                  sx={{ display: 'inline-block' }}
                />
              ) : (
                getDisplaySubtotal()
              )}
            </Typography>
          </div>
        </div>
        {apiPricingData?.total_modifiers_price > 0 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
            }}
          >
            <div style={{ flex: 1 }}>
              <Typography
                className={classes.typographyP}
                variant="body2"
                component="p"
                sx={{ fontFamily: 'Jost, sans-serif' }}
              >
                items Customizations
              </Typography>
            </div>
            <div style={{ flex: 0.4, textAlign: 'right' }}>
              <Typography
                className={classes.typographyH6}
                variant="body2"
                sx={{ fontFamily: 'Jost, sans-serif' }}
              >
                {environment.currency}
                {isCalculatingPrice || isDataChanging ? (
                  <Skeleton
                    variant="text"
                    width={60}
                    sx={{ display: 'inline-block' }}
                  />
                ) : (
                  formatPriceFromCents(apiPricingData?.total_modifiers_price)
                )}
              </Typography>
            </div>
          </div>
        )}
        {orderType === 'delivery' && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
            }}
          >
            <div style={{ flex: 1 }}>
              <Typography
                className={classes.typographyP}
                variant="body2"
                component="p"
                sx={{ fontFamily: 'Jost, sans-serif' }}
              >
                delivery fees
                <InfoOutlinedIcon
                  fontSize="small"
                  sx={{
                    cursor: 'pointer',
                    marginLeft: '8px',
                    verticalAlign: 'middle',
                  }}
                  style={{ direction: 'initial' }}
                  onClick={() => setOpenDeliveryfees(true)}
                />
              </Typography>
            </div>
            <div style={{ flex: 0.4, textAlign: 'right' }}>
              <Typography
                className={classes.typographyH6}
                variant="body2"
                sx={{ fontFamily: 'Jost, sans-serif' }}
              >
                {environment.currency}
                {isCalculatingPrice || isDataChanging ? (
                  <Skeleton
                    variant="text"
                    width={60}
                    sx={{ display: 'inline-block' }}
                  />
                ) : (
                  getDisplayDelivery()
                )}
              </Typography>
            </div>
          </div>
        )}

        {showTax && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '16px',
            }}
          >
            <div style={{ flex: 1 }}>
              <Typography
                className={classes.typographyP}
                variant="body2"
                component="p"
                sx={{ fontFamily: 'Jost, sans-serif' }}
              >
                {orderType === 'pickup' || orderType === 'dinein' ? (
                  <span>Estimated Taxes:</span>
                ) : (
                  <span>Estimated Taxes:</span>
                )}{' '}
                <InfoOutlinedIcon
                  fontSize="small"
                  sx={{
                    cursor: 'pointer',
                  }}
                  style={{ direction: 'initial' }}
                  onClick={() => setOpentaxes(true)}
                />
                <FeesDialog
                  opentaxes={opentaxes}
                  setOpentaxes={setOpentaxes}
                  dataStore={dataStore}
                  orderType={orderType}
                  serviceFees={serviceFees}
                  total={total}
                  data={data}
                  backendTax={formatPriceFromCents(apiPricingData?.tax) || 0}
                  taxesText={apiPricingData?.taxes_text || ''}
                />
                {/* Delivery fees dialog (reusable FeesDialog in delivery mode) */}
                <FeesDialog
                  opentaxes={openDeliveryfees}
                  setOpentaxes={setOpenDeliveryfees}
                  dialogType="delivery"
                  storeData={
                    orderType === 'delivery'
                      ? filteredStore || dataStore || data
                      : dataStore
                  }
                  orderType={orderType}
                />
              </Typography>
            </div>
            <div style={{ flex: 0.4, textAlign: 'right' }}>
              <Typography
                variant="body2"
                sx={{ fontFamily: 'Jost, sans-serif' }}
                className={classes.typographyH6}
              >
                {apiPricingData ? (
                  <p>
                    <span>
                      {environment.currency}
                      {isCalculatingPrice || isDataChanging ? (
                        <Skeleton
                          variant="text"
                          width={60}
                          sx={{ display: 'inline-block' }}
                        />
                      ) : (
                        getDisplayTax()
                      )}
                    </span>
                  </p>
                ) : (
                  <>
                    {orderType === 'dinein' && (
                      <p>
                        <span>
                          {dataStore?.tax?.type === 'amount' ? (
                            <>
                              {environment.currency}
                              {isVerify
                                ? parseFloat(
                                    (
                                      (dataStore?.tax?.tax || 0) +
                                      (dataStore?.service_fee?.dineIn || 0)
                                    )
                                      .toString()
                                      .replaceAll(',', '')
                                  ).toFixed(2)
                                : parseFloat(
                                    (
                                      (dataStore?.tax?.tax || 0) +
                                      (dataStore?.service_fee?.dineIn || 0)
                                    )
                                      .toString()
                                      .replaceAll(',', '')
                                  ).toFixed(2)}
                            </>
                          ) : (
                            <>
                              {environment.currency}
                              {isVerify
                                ? parseFloat(
                                    (
                                      (dataStore?.tax?.tax || 0) *
                                        (total - discount) +
                                      (dataStore?.service_fee?.dineIn || 0)
                                    )
                                      .toString()
                                      .replaceAll(',', '')
                                  ).toFixed(2)
                                : parseFloat(
                                    (
                                      (dataStore?.tax?.tax || 0) * total +
                                      (dataStore?.service_fee?.dineIn || 0)
                                    )
                                      .toString()
                                      .replaceAll(',', '')
                                  ).toFixed(2)}
                            </>
                          )}
                        </span>
                      </p>
                    )}
                    {orderType === 'delivery' && (
                      <p style={{ marginLeft: '5px' }}>
                        <span>
                          {data.id === addressStore ? (
                            <span>
                              {dataStore?.tax?.type === 'amount' ? (
                                <>
                                  {environment.currency}
                                  {isVerify
                                    ? parseFloat(
                                        ((dataStore?.tax?.tax || 0) * total)
                                          .toString()
                                          .replaceAll(',', '')
                                      ).toFixed(2)
                                    : parseFloat(
                                        ((dataStore?.tax?.tax || 0) * total)
                                          .toString()
                                          .replaceAll(',', '')
                                      ).toFixed(2)}
                                </>
                              ) : (
                                <>
                                  {environment.currency}
                                  {isVerify
                                    ? parseFloat(
                                        (
                                          (dataStore?.tax?.tax || 0) *
                                          (total - discount)
                                        )
                                          .toString()
                                          .replaceAll(',', '')
                                      ).toFixed(2)
                                    : parseFloat(
                                        ((dataStore?.tax?.tax || 0) * total)
                                          .toString()
                                          .replaceAll(',', '')
                                      ).toFixed(2)}
                                </>
                              )}
                            </span>
                          ) : (
                            ''
                          )}
                        </span>
                      </p>
                    )}
                    {orderType === 'pickup' && (
                      <p>
                        <span>
                          {defaultStorefee?.percentage_type === 'amount' ? (
                            <>
                              {environment.currency}
                              {isVerify
                                ? parseFloat(
                                    (
                                      (defaultStorefee?.tax?.tax || 0) +
                                      (defaultStorefee?.service_fee?.pickUp ||
                                        0)
                                    )
                                      .toString()
                                      .replaceAll(',', '')
                                  ).toFixed(2)
                                : parseFloat(
                                    (
                                      (defaultStorefee?.tax?.tax || 0) +
                                      (defaultStorefee?.service_fee?.pickUp ||
                                        0)
                                    )
                                      .toString()
                                      .replaceAll(',', '')
                                  ).toFixed(2)}
                            </>
                          ) : (
                            <>
                              {environment.currency}
                              {isVerify
                                ? parseFloat(
                                    (
                                      (defaultStorefee?.tax?.tax || 0) *
                                        (total - discount) +
                                      (defaultStorefee?.service_fee?.pickUp ||
                                        0)
                                    )
                                      .toString()
                                      .replaceAll(',', '')
                                  ).toFixed(2)
                                : parseFloat(
                                    (
                                      (defaultStorefee?.tax?.tax || 0) * total +
                                      (defaultStorefee?.service_fee?.pickUp ||
                                        0)
                                    )
                                      .toString()
                                      .replaceAll(',', '')
                                  ).toFixed(2)}
                            </>
                          )}
                        </span>
                      </p>
                    )}
                  </>
                )}
              </Typography>
            </div>
          </div>
        )}

        {orderType !== 'dinein' && (
          <>
            {checkLicenses('tips') && showTip && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '16px',
                }}
              >
                <div style={{ flex: 1 }}>
                  <Typography
                    className={classes.typographyP}
                    variant="body2"
                    component="p"
                    sx={{ fontFamily: 'Jost, sans-serif' }}
                  >
                    {t('confirmPage.tip')}
                  </Typography>
                </div>
                <div style={{ flex: 0.4, textAlign: 'right' }}>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: 'Jost, sans-serif' }}
                    className={classes.typographyH6}
                  >
                    {environment.currency}
                    {formatPriceFromCents(tipAmount)}
                  </Typography>
                </div>
              </div>
            )}
          </>
        )}
        {apiPricingData?.onlineFee > 0 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
            }}
          >
            <div style={{ flex: 1 }}>
              <Typography
                className={classes.typographyP}
                variant="body2"
                component="p"
                sx={{ fontFamily: 'Jost, sans-serif' }}
              >
                online ordering fee
              </Typography>
            </div>
            <div style={{ flex: 0.4, textAlign: 'right' }}>
              <Typography
                className={classes.typographyH6}
                variant="body2"
                sx={{ fontFamily: 'Jost, sans-serif' }}
              >
                {environment.currency}
                {isCalculatingPrice || isDataChanging ? (
                  <Skeleton
                    variant="text"
                    width={60}
                    sx={{ display: 'inline-block' }}
                  />
                ) : (
                  formatPriceFromCents(apiPricingData?.onlineFee)
                )}
              </Typography>
            </div>
          </div>
        )}
        {apiPricingData?.total_discounts > 0 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
            }}
          >
            <div style={{ flex: 1 }}>
              <Typography
                component="a"
                variant="body1"
                sx={{
                  fontFamily: '"Jost", "Helvetica", "Arial", sans-serif',
                  display: 'flex',
                  color: '#EA4B48',
                }}
              >
                Total Discounts:
              </Typography>
            </div>
            <div style={{ flex: 0.4, textAlign: 'right' }}>
              <Typography
                className={classes.typographyH6}
                variant="body2"
                sx={{ fontFamily: 'Jost, sans-serif', color: '#EA4B48' }}
              >
                -{environment.currency}
                {isCalculatingPrice || isDataChanging ? (
                  <Skeleton
                    variant="text"
                    width={60}
                    sx={{ display: 'inline-block' }}
                  />
                ) : (
                  formatPriceFromCents(apiPricingData?.total_discounts)
                )}
              </Typography>
            </div>
          </div>
        )}
        {apiPricingData?.discount > 0 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
            }}
          >
            <div style={{ flex: 1 }}>
              <Typography
                component="a"
                variant="body1"
                sx={{
                  fontFamily: '"Jost", "Helvetica", "Arial", sans-serif',
                  display: 'flex',
                  color: '#EA4B48',
                }}
              >
                Discount:
              </Typography>
            </div>
            <div style={{ flex: 0.4, textAlign: 'right' }}>
              <Typography
                className={classes.typographyH6}
                variant="body2"
                sx={{ fontFamily: 'Jost, sans-serif', color: '#EA4B48' }}
              >
                -{environment.currency}
                {isCalculatingPrice || isDataChanging ? (
                  <Skeleton
                    variant="text"
                    width={60}
                    sx={{ display: 'inline-block' }}
                  />
                ) : (
                  formatPriceFromCents(apiPricingData?.discount)
                )}
              </Typography>
            </div>
          </div>
        )}
        {apiPricingData?.loyaltyDiscount > 0 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
            }}
          >
            <div style={{ flex: 1 }}>
              <Typography
                component="a"
                variant="body1"
                sx={{
                  fontFamily: '"Jost", "Helvetica", "Arial", sans-serif',
                  display: 'flex',
                  color: '#EA4B48',
                }}
              >
                Loyalty Discounts:
              </Typography>
            </div>
            <div style={{ flex: 0.4, textAlign: 'right' }}>
              <Typography
                className={classes.typographyH6}
                variant="body2"
                sx={{ fontFamily: 'Jost, sans-serif', color: '#EA4B48' }}
              >
                -{environment.currency}
                {isCalculatingPrice || isDataChanging ? (
                  <Skeleton
                    variant="text"
                    width={60}
                    sx={{ display: 'inline-block' }}
                  />
                ) : (
                  formatPriceFromCents(apiPricingData?.loyaltyDiscount)
                )}
              </Typography>
            </div>
          </div>
        )}
        {/* Show when user has applied a gift card (including $0 balance so they see why nothing discounted) */}
        {appliedGiftCard && (() => {
          // Amount actually applied to this order (deduction) = total before GC − remaining to pay
          const amountAppliedCents =
            apiPricingData?.totalWithTip != null && apiPricingData?.remainingAmount != null
              ? Math.max(0, (apiPricingData.totalWithTip || 0) - (apiPricingData.remainingAmount || 0))
              : apiPricingData?.giftCardBalance || 0;
          // Balance left on card = full balance on card − amount applied (when API sends full balance)
          const leftBalanceCents = Math.max(
            0,
            (apiPricingData?.giftCardBalance || 0) - amountAppliedCents
          );
          const showLeftBalance = leftBalanceCents > 0;
          const hasNoBalance = amountAppliedCents === 0;
          return (
            <div style={{ marginBottom: '16px' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div style={{ flex: 1 }}>
                  <Typography
                    component="span"
                    variant="body1"
                    sx={{
                      fontFamily: '"Jost", "Helvetica", "Arial", sans-serif',
                      display: 'flex',
                      color: hasNoBalance ? '#5C6B7A' : '#EA4B48',
                    }}
                  >
                    Gift Card Applied:
                  </Typography>
                </div>
                <div style={{ flex: 0.4, textAlign: 'right' }}>
                  <Typography
                    className={classes.typographyH6}
                    variant="body2"
                    sx={{
                      fontFamily: 'Jost, sans-serif',
                      color: hasNoBalance ? '#5C6B7A' : '#EA4B48',
                    }}
                  >
                    {hasNoBalance ? '' : '-'}
                    {environment.currency}
                    {isCalculatingPrice || isDataChanging ? (
                      <Skeleton
                        variant="text"
                        width={60}
                        sx={{ display: 'inline-block' }}
                      />
                    ) : (
                      formatPriceFromCents(amountAppliedCents)
                    )}
                  </Typography>
                </div>
              </div>
              {showLeftBalance && (
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: '"Jost", "Helvetica", "Arial", sans-serif',
                    fontSize: '12px',
                    color: '#5C6B7A',
                    marginTop: '4px',
                    marginLeft: 0,
                  }}
                >
                  Balance left on card: {environment.currency}
                  {isCalculatingPrice || isDataChanging ? (
                    <Skeleton variant="text" width={40} sx={{ display: 'inline-block' }} />
                  ) : (
                    formatPriceFromCents(leftBalanceCents)
                  )}
                </Typography>
              )}
              {hasNoBalance && !isCalculatingPrice && !isDataChanging && (
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: '"Jost", "Helvetica", "Arial", sans-serif',
                    fontSize: '12px',
                    color: '#5C6B7A',
                    marginTop: '4px',
                    marginLeft: 0,
                    fontStyle: 'italic',
                  }}
                >
                  This card has no balance — no discount was applied.
                </Typography>
              )}
            </div>
          );
        })()}
        <div
          style={{ borderBottom: '1px solid #BDC0C3', margin: '16px 0' }}
        ></div>

        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ flex: 1 }}>
            <Typography
              className={classes.typographyP}
              variant="h6"
              component="div"
              style={{ fontFamily: 'Jost, sans-serif', fontSize: '18px' }}
            >
              {t('basket.total')}
            </Typography>
          </div>
          <div style={{ flex: 0.4, textAlign: 'right' }}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontWeight: 'bold',
                fontSize: '18px',
              }}
              className={classes.typographyH6}
            >
              {environment.currency}
              {isCalculatingPrice || isDataChanging ? (
                <Skeleton
                  variant="text"
                  width={80}
                  sx={{ display: 'inline-block' }}
                />
              ) : (
                getDisplayTotal()
              )}
            </Typography>
          </div>
        </div>
      </div>
    </Stack>
  );
};

export default OrderSummary;
